import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Button, Col, message, Radio, RadioChangeEvent, Row, Space, Tooltip, Typography } from 'antd';
import TranslationKey from 'src/i18n/translation';
import { InfoCircleOutlined } from '@ant-design/icons';
import { AssetService } from 'src/services/assetService';
import { HttpStatus } from 'src/constant/responseStatus';
import { AssetPublishModel } from 'src/models/assetModel';
import { ExtendedLicenseModel, StandardLicenseModel } from 'src/models/LicenseModel';
import { StandardLicense, ExtendedLicense } from '../../constant/licenseConstants';
import { StringConstants } from 'src/constant/stringConstants';
import { LicenseService } from 'src/services/licenseService';
import { AssetType } from 'src/constant/assetConstants';
import { UserAssetLicenseResponse } from 'src/models/responses/userAssetLicenseResponse';
import { useSessionContext } from 'src/context/session-context';
import { UserAssetLicenseService } from 'src/services/userAssetLicenseService';
import { ExclusiveLicenseModel } from 'src/models/ExclusiveLicenseModel';
import { AccountService } from 'src/services/accountService';
import VideoControl from '../controls/VideoControl';
const { Title, Text } = Typography;

interface AddToCartInterface {
  isModalOpen: boolean;
  onCloseModal: any;
  assetId: string;
}

export default function AddToCartModal({
  isModalOpen = false,
  onCloseModal: onCloseModal,
  assetId,
}: AddToCartInterface) {
  const [asset, setAsset] = useState<AssetPublishModel>();
  const [orderAsset, setOrderAsset] = useState<AssetPublishModel>({} as AssetPublishModel);
  const [standardLicense, setStandardLicense] = useState<StandardLicenseModel[]>();
  const [extendedLicense, setExtendedLicense] = useState<ExtendedLicenseModel[]>();
  const [stdValue, setStdValue] = useState<StandardLicenseModel>();
  const [extValue, setExtValue] = useState<ExtendedLicenseModel>();
  const [totalPrice, setTotalPrice] = useState(0);
  const [, setStandardLicenseVip] = useState<StandardLicenseModel>({} as StandardLicenseModel);
  const [hasUserLicense, setHasUserLicense] = useState(false);
  const [userAssetLicenses, setUserAssetLicenses] = useState<UserAssetLicenseResponse[]>([]);
  const [userAssetLicenseId, setUserAssetLicenseId] = useState('');
  const { getOrderAssets, setOrderAssets: updateOrderAssets, userProfile, getCountOrder } = useSessionContext();
  const [isExclusive, setIsExclusive] = useState(false);
  const [isShowContact, setIsShowContact] = useState(false);
  const [isSameAccount, setIsSameAccount] = useState(false);
  const [cartLoadings, setCartLoadings] = useState<boolean>();

  useEffect(() => {
    if (isModalOpen && assetId) {
      getAssetPublishedByIdAsync(assetId);
      onCheckExclusiveAsync(assetId);
    }
  }, [isModalOpen, assetId]);

  useEffect(() => {
    if (stdValue && extValue) {
      calculateTotalPrice();

      if (userAssetLicenses) {
        checkLicense(stdValue.id!, extValue.id!);
      }
    }

  }, [stdValue, extValue, userAssetLicenses]);

  useEffect(() => {
    if (standardLicense) {
      onGetUserAssetLicenseAsync(assetId);
    }

  }, [standardLicense]);

  const onGetUserAssetLicenseAsync = async (assetId: string) => {
    const { data: responseData, status: responseStatus } = await new UserAssetLicenseService().getByAssetIdAsync(assetId);

    if (responseStatus !== HttpStatus.OK) {
      setDefaultSelectLicense();
      return;
    }

    setUserAssetLicenses(responseData);
    if (responseData) {
      setDefaultSelectLicense(responseData);
    }
  };

  const onGetArtistByUserIdAsync = async (userId: string) => {
    const { data: responseData, status: responseStatus } = await new AccountService().getArtistById(userId);

    if (responseStatus !== HttpStatus.OK) {
      return;
    }

    if (responseData && userProfile && userProfile.userId) {
      setIsSameAccount(userProfile.userId === responseData.artist.id);
    }
  };

  const setDefaultSelectLicense = (datas?: UserAssetLicenseResponse[]) => {
    if (datas && datas.length > 0) {
      const largeLicenses = datas.filter(x => (x.standardLicense.code === StandardLicense.XL || x.standardLicense.code === StandardLicense.UHD));
      const smallLicenses = datas.filter(x => (x.standardLicense.code === StandardLicense.SM || x.standardLicense.code === StandardLicense.HD));
      if (largeLicenses && largeLicenses.length > 0) {
        const maxExt = getMaxExtendedLicense(largeLicenses);
        setStdValue(maxExt!.standardLicense);
        setExtValue(maxExt!.extendedLicenses[0]);
        setUserAssetLicenseId(maxExt!.standardLicenseId);
        return;
      }

      if (smallLicenses && smallLicenses.length > 0) {
        const maxExt = getMaxExtendedLicense(smallLicenses);
        setStdValue(maxExt!.standardLicense);
        setExtValue(maxExt!.extendedLicenses[0]);
        setUserAssetLicenseId(maxExt!.standardLicenseId);
        return;
      }
    }
    else {
      // Select largest size available (XL for images, UHD for videos)
      const filteredStdLicenses = standardLicense?.filter(std => std.assetType === asset?.assetType);
      let defaultStdLicense: StandardLicenseModel | undefined;

      if (asset?.assetType === AssetType.IMAGE) {
        // Priority: XL → SM
        defaultStdLicense = filteredStdLicenses?.find(std => std.code === StandardLicense.XL) 
          || filteredStdLicenses?.[0];
      } else if (asset?.assetType === AssetType.VIDEO) {
        // Priority: UHD → HD
        defaultStdLicense = filteredStdLicenses?.find(std => std.code === StandardLicense.UHD) 
          || filteredStdLicenses?.[0];
      } else {
        defaultStdLicense = filteredStdLicenses?.[0];
      }

      if (defaultStdLicense) {
        setStdValue(defaultStdLicense);
      }
      setExtValue(extendedLicense?.filter((x) => x.assetType === asset?.assetType)[0]);
    }
  }

  const getMaxExtendedLicense = (userAssetLicenseResponses: UserAssetLicenseResponse[]) => {
    if (!userAssetLicenseResponses || userAssetLicenseResponses.length === 0) {
      return null;
    }

    let maxValue = userAssetLicenseResponses[0];

    for (const license of userAssetLicenseResponses) {
      if (license.extendedLicenses[0].sequence > maxValue.extendedLicenses[0].sequence) {
        maxValue = license;
      }
    }

    return maxValue;
  }

  const onCheckExclusiveAsync = async (assetId: string) => {
    const {
      data: responseData,
      status: responseStatus,
    } =
      await new UserAssetLicenseService().getCalculateExclusiveAsync(assetId);

    if (responseStatus !== HttpStatus.OK) {
      setIsExclusive(false);
      return;
    }

    const exclusiveData = responseData as ExclusiveLicenseModel;

    if (exclusiveData && exclusiveData.applicationUser) {
      const exclusiveDate = new Date(exclusiveData.extendedLicenseExpireDate);
      const nowDate = new Date();
      if (exclusiveData.exclusive > 0 && nowDate <= exclusiveDate) {
        setIsExclusive(true);

        if (userProfile && userProfile.userId === exclusiveData.applicationUser.id) {
          setIsExclusive(false);
        }
      }
    }
  };

  const getAssetPublishedByIdAsync = async (assetId: string) => {
    const {
      data: responseData,
      status: responseStatus
    } = await new AssetService().getAssetPublishAsync(assetId);

    if (responseStatus !== HttpStatus.OK) {
      console.error('*** Error fetching asset published data', responseData);

      return;
    }

    onSetAssetToOrder(responseData);
    setAsset(responseData);

    if (responseData == undefined || responseData == null) {
      debugger
      return;
    }

    onGetLicenseAsync(responseData);
    onGetArtistByUserIdAsync(responseData.userId);
    onGetUserAssetLicenseAsync(responseData.id);
  };

  const calculateTotalPrice = () => {
    if (stdValue && extValue) {
      setOrderAsset((pre) => ({ ...pre, standardLicense: stdValue }));
      setOrderAsset((pre) => ({ ...pre, extendedLicense: extValue }));
      setOrderAsset((pre) => ({
        ...pre,
        price:
          userAssetLicenses.filter((x) => x.standardLicenseId === stdValue.id).length > 0
            ? extValue.price
            : stdValue.price + extValue.price,
        isHasLicense: userAssetLicenses.filter((x) => x.standardLicenseId === stdValue.id).length > 0,
      }));

      if (userAssetLicenses.filter((x) => x.standardLicenseId === stdValue.id).length > 0) {
        setTotalPrice(extValue.price);
      } else {
        setTotalPrice(stdValue.price + extValue.price);
      }

      setIsShowContact(extValue.code === ExtendedLicense.EX3PLUS);
    }
  };

  const onChangeStdLicense = ({ target: { value } }: RadioChangeEvent) => {
    const stdLicense = standardLicense?.filter(e => e.id === value)[0];

    if (stdLicense) {
      setStdValue(stdLicense);
      checkLicense(value, extValue?.id!);
    }
  };

  const onChangeExtLicense = ({ target: { value } }: RadioChangeEvent) => {
    const extLicense = extendedLicense?.filter(e => e.id === value)[0];

    if (extLicense) {
      setExtValue(extLicense);
    }
  };

  const checkLicense = (
    stdId: string,
    extId: string) => {
    setHasUserLicense(false);

    if (userAssetLicenses) {
      const userStdLicenses =
        userAssetLicenses.filter((ual) => ual.standardLicenseId === stdId);

      const extLicense =
        extendedLicense?.filter(
          (etl) =>
            etl.id === extId &&
            etl.assetType === asset?.assetType &&
            (etl.code === ExtendedLicense.NONE || etl.code === ExtendedLicense.VIP)
        )[0];

      const extLicenseMPFR =
        extendedLicense?.filter(
          (x) => x.id === extId && x.assetType === asset?.assetType && x.code === ExtendedLicense.MPFR
        )[0];

      if (userStdLicenses.length > 0) {
        setUserAssetLicenseId(userStdLicenses[0].id);

        //Check None Extended Licenses.
        if (
          extLicense ||
          (extLicenseMPFR &&
            userStdLicenses.filter((x) => x.extendedLicenses.filter((e) => e.id === extLicenseMPFR.id).length > 0)
              .length > 0)
        ) {
          setHasUserLicense(true);
          return;
        }
      } else {
        setUserAssetLicenseId('');
      }
    }

    setOrderAsset((pre) => ({
      ...pre,
      isHasLicense: userAssetLicenses.filter((ual) => ual.standardLicenseId === stdId).length > 0,
    }));
    calculateTotalPrice();
  };

  const onGetLicenseAsync = async (asset: AssetPublishModel) => {
    const { data: responseStdData, status: responseStdStatus } = await new LicenseService().GetStandardLicenseByType(
      asset?.assetType!
    );

    if (responseStdStatus !== HttpStatus.OK) {
      return;
    }

    const { data: responseExtData, status: responseExtStatus } = await new LicenseService().GetExtendedLicenseByType(
      asset?.assetType!
    );

    if (responseExtStatus !== HttpStatus.OK) {
      return;
    }

    setStandardLicense(responseStdData);

    if (asset?.assetType === AssetType.IMAGE) {
      setStandardLicenseVip(responseStdData.filter((x) => x.code === StandardLicense.SM)[0]);
      if (!asset.hasSMLicense) {
        setStandardLicense(responseStdData.filter((x) => x.code !== StandardLicense.SM));
      }

      if (!asset.hasXLLicense) {
        setStandardLicense(responseStdData.filter((x) => x.code !== StandardLicense.XL));
      }
    } else {
      setStandardLicenseVip(responseStdData.filter((x) => x.code === StandardLicense.HD)[0]);

      if (!asset?.hasHDLicense) {
        setStandardLicense(responseStdData.filter((x) => x.code !== StandardLicense.HD));
      }

      if (!asset?.hasUHDLicense) {
        setStandardLicense(responseStdData.filter((x) => x.code !== StandardLicense.UHD));
      }
    }

    //TODO:filter not vip
    setExtendedLicense(responseExtData.filter((x) => x.code !== ExtendedLicense.VIP));

    checkLicense(
      responseStdData.filter((x) => x.assetType === asset?.assetType)[0].id,
      responseExtData.filter((x) => x.assetType === asset?.assetType)[0].id
    );
  };

  const enterLoading = () => {
    setCartLoadings(true);

    setTimeout(() => {
      setCartLoadings(false);
    }, 2000);
  };

  const onAddToCart = () => {
    enterLoading();

    let orders = getOrderAssets();

    if (!orders) {
      orders = [];
    }

    //Check dubplicate
    const dubIndex = orders.findIndex((order) => order.id === orderAsset.id);

    const isExtendedLicense = orderAsset.extendedLicense.code !== ExtendedLicense.NONE;

    //Check Nolicense
    const nolicenseIndex = orders.findIndex(
      (order) =>
        order.id === orderAsset.id &&
        order.standardLicense.id === orderAsset.standardLicense.id &&
        order.extendedLicense.code === ExtendedLicense.NONE
    );

    //Check has Extendedlicense
    const hasExtlicense =
      orders.filter(
        (order) =>
          order.id === orderAsset.id &&
          order.standardLicense.id === orderAsset.standardLicense.id &&
          order.extendedLicense.code !== ExtendedLicense.NONE
      ).length > 0;

    //Check has add ExtendedLicense but not noneExtenededlicense.
    if (isExtendedLicense && nolicenseIndex > -1) {
      orders.splice(nolicenseIndex, 1);
    }

    //Check is select none but has extendedlicense or duplicate
    if ((!isExtendedLicense && hasExtlicense) || dubIndex > -1) {
      orders.splice(dubIndex, 1);
    }

    orders.push(orderAsset);
    updateOrderAssets(orders);

    getCountOrder();

    message.open({
      type: 'success',
      content: TranslationKey('cart.addItemToCartSuccess'),
    });

    onCloseModal();
  };

  const onSetAssetToOrder = (data: AssetPublishModel) => {
    let orderAsset: AssetPublishModel;
    orderAsset = {
      id: data.id,
      assetType: data.assetType,
      code: data.code,
      name: data.name,
      description: data.description,
      thumbnailImageUrl: data.thumbnailImageUrl,
      thumbnailVideoUrl: data.thumbnailVideoUrl,
      thumbnailVideoCoverUrl: data.thumbnailVideoCoverUrl,
      hasSMLicense: data.hasSMLicense,
      hasXLLicense: data.hasXLLicense,
      hasHDLicense: data.hasHDLicense,
      hasUHDLicense: data.hasUHDLicense,
      publishDate: data.publishDate,
      hasCollection: data.hasCollection,
      keywords: data.keywords,
      userId: data.userId,
      standardLicense: data.standardLicense,
      extendedLicense: data.extendedLicense,
      price: data.price,
      isDisabled: data.isDisabled,
      isHasLicense: hasUserLicense,
      imageOrientation: data.imageOrientation,
      usage: data.usage,
      width: data.width,
      height: data.height,
    };

    setOrderAsset(orderAsset);
  };

  return (
    <Modal
      size='lg'
      show={isModalOpen}
      dialogClassName='fav-modal'>
      <>
        {
          asset &&
          <Modal.Body>
            <Row className='product'>
              <Col span={12} offset={6} className='price'>
                <div className='img-info-modal'>
                  {
                    asset?.assetType === AssetType.IMAGE ? (
                      <img
                        src={asset?.thumbnailImageUrl}
                        className='product-img'
                        alt={asset.name}
                      />
                    ) : (
                      <VideoControl
                        src={asset?.thumbnailVideoUrl}
                        muted={true}
                        loop={true}
                        className='product-vdo'
                        controls={true}
                      />
                    )
                  }
                </div>
              </Col>
              <Col span={24} className='text-light'>
                <Title
                  level={5}
                  className='text-gray mt-4'
                >
                  {asset?.code}
                  {asset?.name && <> - {asset?.name}</>}
                </Title>
              </Col>
              <Col span={24} lg={12} className='text-light'>
                <Title
                  level={5}
                  className='text-light mt-4 mb-0'
                >
                  {TranslationKey('assetDetail.totalPrice')}
                </Title>
                <Title
                  level={1}
                  className='text-light mt-2'
                >
                  {
                    totalPrice.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })
                  }
                  <Text className='text-light'> THB (Excluding VAT)</Text>
                </Title>
                <Title
                  level={5}
                  className='text-light mt-4 mb-0'>
                  {TranslationKey('assetDetail.selectLicense')}
                  <Tooltip
                    title={
                      <div>
                        <div>
                          Use this license for unlimited copies, viewers, and worldwide, in perpetuity.</div>
                        <br />
                        <div>
                          This includes marketing materials, packaging of products, presentations, catalogues and e-catalogues, books and e-books, magazines and online magazines, newspapers and online newspaper, websites, mobile applications, albums and online albums, playlist covers, video games, and social media platforms.</div>
                      </div>
                    }>
                    <InfoCircleOutlined style={{ paddingLeft: '0.5rem' }} />
                  </Tooltip>
                </Title>
                <Radio.Group
                  onChange={onChangeStdLicense}
                  value={stdValue?.id}
                  optionType='button'
                  className='mt-3'
                  size='large'
                >
                  {standardLicense?.map((s, index) => (
                    <Radio
                      value={s.id}
                      key={index}
                    >
                      <p className='fs-md'>{s.name}</p>
                      <p className='fs-sm'>
                        {StringConstants.BAHT +
                          ' ' +
                          s.price.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                          })}
                      </p>
                    </Radio>
                  ))}
                </Radio.Group>
              </Col>
              <Col span={24} lg={12} className='text-light'>
                <Title level={5}
                  className='text-light mt-4 mb-0'>
                  {TranslationKey('assetDetail.selectExtendedLicense')}
                  <Tooltip
                    title={
                      <div>
                        <div style={{ fontWeight: 'bold' }}>Merchandise Resale/Distribution</div>
                        <br />
                        <div>Use this license for the product for sale or promotional distribution license package which allows incorporation of the Arts and Contents or its derivatives into products e.g. calendars, greeting cards, keyrings, mugs, postcards, templates, textiles, toys, wall art, and any other physical reproduction for resale or distribution whether for free or with remuneration.</div>
                        <br />
                        <br />
                        <div style={{ fontWeight: 'bold' }}>Exclusive Market Freeze 6 Months to 3 Years</div>
                        <br />
                        <div>This guarantees exclusivity for a specified length of time from the date of the Arts and Contents purchase and includes free unlimited copies, viewers, and worldwide, in perpetuity. An Exclusive Market Freeze license is not refundable and is only offered as an add-on to the X-Large Standard License.</div>
                      </div>
                    }>
                    <InfoCircleOutlined style={{ paddingLeft: '0.5rem' }} />
                  </Tooltip>
                </Title>
                <div className='bg-outline-gray boxradius mt-2'>
                  <Radio.Group
                    onChange={onChangeExtLicense}
                    value={extValue?.id}
                    className='custom-color-radio'
                    size='large'
                  >
                    <Space
                      direction='vertical'
                      size={'middle'}
                    >
                      {extendedLicense
                        ?.filter((x) => x.assetType === asset?.assetType)
                        .map((e, index) => (
                          <Radio
                            value={e.id}
                            className='text-gray'
                            key={index}
                          >
                            +
                            <span className='text-light mx-1 fw-bold'>
                              {e.price > 0
                                ? StringConstants.BAHT +
                                ' ' +
                                e.price.toLocaleString(undefined, {
                                  maximumFractionDigits: 2,
                                })
                                : ''}
                            </span>
                            {e.price > 0 && e.name !== 'Merchandise Product for Resale'
                              ? <Tooltip
                                title="This guarantees exclusivity for a specified length of time from the date of the Arts and Contents purchase and includes free unlimited copies, viewers, and worldwide, in perpetuity. An Exclusive Market Freeze license is not refundable and is only offered as an add-on to the X-Large Standard License.
                                                ">
                                {e.name}
                              </Tooltip> : <>{e.name}</>}
                          </Radio>
                        ))}
                    </Space>
                  </Radio.Group>
                </div>
              </Col>
              <Col span={24} className='text-light'>
                <Row
                  gutter={[8, 8]}
                  className='mt-3'
                  justify='center'
                >
                  <>
                    <Col span={12}>
                      <Button
                        type='primary'
                        shape='round'
                        className='btn-outline-border'
                        size='large'
                        onClick={() => onCloseModal()}
                        block
                        loading={cartLoadings}
                      >
                        {TranslationKey('button.cancel')}
                      </Button>
                    </Col>
                    {
                      asset != undefined && !asset.isDisabled && !hasUserLicense && !isExclusive &&
                      (
                        <Col span={12}>
                          <Button
                            type='primary'
                            shape='round'
                            className='btn-primary'
                            size='large'
                            onClick={onAddToCart}
                            block
                            loading={cartLoadings}
                            hidden={isSameAccount}
                          >
                            {TranslationKey('cart.addToCart')}
                          </Button>
                        </Col>
                      )
                    }
                  </>
                </Row>
              </Col>
            </Row>
          </Modal.Body>
        }
      </>
    </Modal>
  );
}
